import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import 'dayjs/locale/vi'
import 'dayjs/locale/zh-cn'
import 'dayjs/locale/en'
import { i18n, type AppLocale } from '@/i18n'
import { formatPhoneDisplay } from '@/shared/utils/phone-validation'
import { interviewHourToPeriod } from '@/shared/utils/interview-period'

dayjs.extend(relativeTime)
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)

/**
 * Contract: DB/API store UTC instants; every operator-facing datetime on FE is
 * Vietnam wall-clock (`Asia/Ho_Chi_Minh`). Never render with the browser's local zone.
 */
export const APP_TIMEZONE = 'Asia/Ho_Chi_Minh'

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/
const TIME_ONLY = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/
const SQL_DATETIME = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/
const DATETIME_LOCAL = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/
const DATETIME_DISPLAY = /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})$/

export const VN_DATE_PATTERN = 'DD/MM/YYYY'

const DAYJS_LOCALE: Record<AppLocale, string> = {
  vi: 'vi',
  zh: 'zh-cn',
  en: 'en',
}

const numberFormatterCache = new Map<string, Intl.NumberFormat>()

function dash(): string {
  const key = 'common.dash'
  if (i18n.global.te(key)) {
    return String(i18n.global.t(key))
  }
  return '—'
}

function syncDayjsLocale(): void {
  const loc = i18n.global.locale.value as AppLocale
  dayjs.locale(DAYJS_LOCALE[loc] ?? 'vi')
}

function gmtOffsetLabel(offsetMinutes: number): string {
  const sign = offsetMinutes >= 0 ? '+' : '-'
  const abs = Math.abs(offsetMinutes)
  const hours = Math.floor(abs / 60)
  const mins = abs % 60
  if (mins === 0) return `GMT${sign}${hours}`
  return `GMT${sign}${hours}:${String(mins).padStart(2, '0')}`
}

function getNumberFormatter(options?: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = JSON.stringify(options ?? {})
  let fmt = numberFormatterCache.get(key)
  if (!fmt) {
    fmt = new Intl.NumberFormat('vi-VN', options)
    numberFormatterCache.set(key, fmt)
  }
  return fmt
}

function calendarDateFromYmd(value: string): string {
  const [y, m, d] = value.slice(0, 10).split('-')
  return `${d}/${m}/${y}`
}

function hasExplicitTimezone(value: string): boolean {
  return /(?:[zZ]|[+-]\d{2}:\d{2}|[+-]\d{4})$/.test(value)
}

/** Parse API value: UTC/offset instants → VN display; naive SQL → UTC wall clock; form-local → VN wall clock. */
function parseApiDate(value: string): dayjs.Dayjs {
  const trimmed = value.trim()
  if (DATE_ONLY.test(trimmed)) {
    return dayjs.tz(trimmed, 'YYYY-MM-DD', APP_TIMEZONE).startOf('day')
  }

  let normalized = trimmed
  if (normalized.includes(' ') && !normalized.includes('T')) {
    normalized = normalized.replace(' ', 'T')
  }

  if (hasExplicitTimezone(normalized)) {
    // UTC (Z / +00:00) — parse as instant, then show in Vietnam
    if (/[zZ]$|[+-]00:00$/.test(normalized)) {
      return dayjs.utc(normalized).tz(APP_TIMEZONE)
    }
    return dayjs(normalized).tz(APP_TIMEZONE)
  }

  // Form model YYYY-MM-DDTHH:mm — already Vietnam local wall clock
  if (DATETIME_LOCAL.test(normalized) && normalized.length === 16) {
    return dayjs.tz(normalized.replace('T', ' '), 'YYYY-MM-DD HH:mm', APP_TIMEZONE)
  }

  // Naive API datetime (no offset) — stored as UTC in DB
  if (SQL_DATETIME.test(normalized)) {
    const bare = normalized.replace(/\.\d+$/, '')
    return dayjs.utc(`${bare}Z`).tz(APP_TIMEZONE)
  }

  const parsed = dayjs(normalized)
  return parsed.isValid() ? parsed.tz(APP_TIMEZONE) : dayjs(NaN)
}

/** Today as YYYY-MM-DD in Vietnam timezone. */
export function todayIsoDate(): string {
  return dayjs().tz(APP_TIMEZONE).format('YYYY-MM-DD')
}

/** Now stamp for filenames in VN timezone. */
export function formatNowStamp(): string {
  return dayjs().tz(APP_TIMEZONE).format('YYYYMMDD-HHmm')
}

/** Tomorrow as YYYY-MM-DD in Vietnam timezone (default start for policy/rule versions). */
export function tomorrowIsoDate(): string {
  return dayjs().tz(APP_TIMEZONE).add(1, 'day').format('YYYY-MM-DD')
}

/** Add calendar days to a YYYY-MM-DD date in Vietnam timezone. */
export function addDaysIsoDate(iso: string, days: number): string {
  const date = String(iso ?? '').slice(0, 10)
  if (!DATE_ONLY.test(date)) return tomorrowIsoDate()
  return dayjs.tz(date, 'YYYY-MM-DD', APP_TIMEZONE).add(days, 'day').format('YYYY-MM-DD')
}

/**
 * Version cutover start: strictly after the source window end (or source start if open-ended),
 * and not before tomorrow.
 */
export function nextVersionEffectiveFromIso(source: {
  effective_from?: string | null
  effective_to?: string | null
}): string {
  const tomorrow = tomorrowIsoDate()
  const sourceTo = String(source.effective_to ?? '').slice(0, 10)
  const sourceFrom = String(source.effective_from ?? '').slice(0, 10)
  const afterOld = DATE_ONLY.test(sourceTo)
    ? addDaysIsoDate(sourceTo, 1)
    : DATE_ONLY.test(sourceFrom)
      ? addDaysIsoDate(sourceFrom, 1)
      : tomorrow
  return afterOld > tomorrow ? afterOld : tomorrow
}

/** Current month as YYYY-MM in Vietnam timezone (e.g. commission period filters). */
export function currentPeriodMonth(): string {
  return dayjs().tz(APP_TIMEZONE).format('YYYY-MM')
}

/**
 * Normalize API date / datetime to calendar YYYY-MM-DD in Vietnam.
 * Date-only strings stay as calendar days; UTC instants convert to ICT date.
 */
export function toBusinessIsoDate(value?: string | null): string {
  if (!value?.trim()) return ''
  const trimmed = value.trim()
  if (DATE_ONLY.test(trimmed)) return trimmed
  if (DATE_ONLY.test(trimmed.slice(0, 10)) && !trimmed.includes('T') && !trimmed.includes(' ')) {
    return trimmed.slice(0, 10)
  }
  const at = parseApiDate(trimmed)
  if (!at.isValid()) {
    const head = trimmed.slice(0, 10)
    return DATE_ONLY.test(head) ? head : ''
  }
  return at.tz(APP_TIMEZONE).format('YYYY-MM-DD')
}

/** Full years from birth date (calendar); uses VN timezone for "today". */
export function ageFromBirthDate(birthDate: string | null | undefined): number | null {
  if (!birthDate?.trim()) return null
  const iso = birthDate.trim().slice(0, 10)
  if (!DATE_ONLY.test(iso)) return null

  const birth = dayjs.utc(iso, 'YYYY-MM-DD')
  if (!birth.isValid()) return null

  const today = dayjs().tz(APP_TIMEZONE).startOf('day')
  const birthDay = birth.startOf('day')
  if (birthDay.isAfter(today)) return null

  let age = today.year() - birthDay.year()
  const monthDiff = today.month() - birthDay.month()
  if (monthDiff < 0 || (monthDiff === 0 && today.date() < birthDay.date())) {
    age -= 1
  }

  return age >= 0 ? age : null
}

export function displayWorkerAge(row: {
  age?: number | null
  birth_date?: string | null
}): number | null {
  const fromBirth = ageFromBirthDate(row.birth_date)
  if (fromBirth !== null) return fromBirth
  return row.age ?? null
}

function coerceNumber(value: number | string | null | undefined): number | null {
  if (value == null || value === '') return null
  const n = typeof value === 'number' ? value : Number(String(value).replace(/[^\d.-]/g, ''))
  return Number.isFinite(n) ? n : null
}

/** Laravel `decimal:2` / plain API money (e.g. "18000.00") — not vi-VN grouping. */
const API_DECIMAL_MONEY = /^-?\d+\.\d{1,2}$/

/** Parse grouped money text (e.g. 6.000.000) to integer VND. */
export function parseMoneyInput(value: string | number | null | undefined): number | null {
  if (value === '' || value == null) return null
  if (typeof value === 'number') return Number.isFinite(value) ? Math.round(value) : null
  const trimmed = String(value).trim()
  // Keep "18000.00" as 18000 — stripping `\D` would turn it into 1800000.
  if (API_DECIMAL_MONEY.test(trimmed)) {
    const n = Number(trimmed)
    return Number.isFinite(n) ? Math.round(n) : null
  }
  const digits = trimmed.replace(/\D/g, '')
  if (!digits) return null
  const n = Number(digits)
  return Number.isFinite(n) ? n : null
}

/** Format money for text inputs — vi-VN grouping without currency suffix. */
export function formatMoneyInputDisplay(value: string | number | null | undefined): string {
  const n = parseMoneyInput(value)
  if (n == null) return ''
  return formatNumber(n)
}

/** Parse grouped integer text (e.g. 1.234) to number. */
export function parseIntegerInput(value: string | number | null | undefined): number | null {
  return parseMoneyInput(value)
}

/** Format integer/count for text inputs — vi-VN grouping without suffix. */
export function formatIntegerInputDisplay(value: string | number | null | undefined): string {
  return formatMoneyInputDisplay(value)
}

/** DD/MM/YYYY — calendar dates without TZ shift; UTC instants shown in VN timezone. */
export function formatDate(value?: string | null): string {
  if (!value) return dash()
  syncDayjsLocale()
  const trimmed = value.trim()
  if (DATE_ONLY.test(trimmed)) return calendarDateFromYmd(trimmed)
  const at = parseApiDate(trimmed)
  if (!at.isValid()) return dash()
  return at.format('DD/MM/YYYY')
}

/** `DD/MM/YYYY → DD/MM/YYYY` | partial ranges | openLabel when both empty. */
export function formatEffectiveDateRange(
  from?: string | null,
  to?: string | null,
  openLabel?: string,
): string {
  const empty = openLabel ?? dash()
  const start = from?.trim() ? formatDate(from) : ''
  const end = to?.trim() ? formatDate(to) : ''
  const fromLabel = start && start !== dash() ? start : ''
  const toLabel = end && end !== dash() ? end : ''
  if (fromLabel && toLabel) return `${fromLabel} → ${toLabel}`
  if (fromLabel) return `${fromLabel} → ${dash()}`
  if (toLabel) return `${dash()} → ${toLabel}`
  return empty
}

/** DD/MM/YYYY HH:mm (VN timezone for UTC API timestamps). */
export function formatDateTime(
  value?: string | null,
  options?: { showTimezone?: boolean; withSeconds?: boolean },
): string {
  if (!value) return dash()
  syncDayjsLocale()
  const trimmed = value.trim()
  if (DATE_ONLY.test(trimmed)) {
    const date = calendarDateFromYmd(trimmed)
    return options?.withSeconds ? `${date} 00:00:00` : `${date} 00:00`
  }
  const at = parseApiDate(trimmed)
  if (!at.isValid()) return dash()
  const pattern = options?.withSeconds ? 'DD/MM/YYYY HH:mm:ss' : 'DD/MM/YYYY HH:mm'
  const formatted = at.format(pattern)
  if (!options?.showTimezone) return formatted
  return `${formatted} (${gmtOffsetLabel(at.utcOffset())})`
}

function interviewPeriodLabelFromHour(hour: number): string {
  const period = interviewHourToPeriod(hour)
  const key =
    period === 'morning'
      ? 'applications.interviewMorning'
      : period === 'afternoon'
        ? 'applications.interviewAfternoon'
        : 'applications.interviewAllDay'
  const fallback = period === 'morning' ? 'Buổi sáng' : period === 'afternoon' ? 'Buổi chiều' : 'Cả ngày'
  return i18n.global.te(key) ? String(i18n.global.t(key)) : fallback
}

/**
 * Interview schedule display — date + Cả ngày / Buổi sáng / Buổi chiều (not HH:mm).
 * Midnight / time_tba maps to all-day, matching supplier intake.
 */
export function formatInterviewScheduledAt(
  value?: string | null,
  options?: { timeTba?: boolean | null },
): string {
  if (!value) return dash()
  syncDayjsLocale()
  const at = parseApiDate(value.trim())
  const looksMidnight =
    at.isValid() && at.hour() === 0 && at.minute() === 0 && at.second() === 0
  const timeTba = options?.timeTba === true || looksMidnight
  const date = formatDate(value)
  if (date === dash()) return dash()
  if (timeTba || !at.isValid()) {
    return `${date} · ${interviewPeriodLabelFromHour(0)}`
  }
  return `${date} · ${interviewPeriodLabelFromHour(at.hour())}`
}

/** HH:mm — VN timezone; also accepts bare HH:mm strings. */
export function formatTime(value?: string | null): string {
  if (!value?.trim()) return dash()
  syncDayjsLocale()
  const trimmed = value.trim()
  const timeMatch = TIME_ONLY.exec(trimmed)
  if (timeMatch && !trimmed.includes('T') && !/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    const hour = Number(timeMatch[1])
    const minute = Number(timeMatch[2])
    if (hour > 23 || minute > 59) return dash()
    return `${timeMatch[1].padStart(2, '0')}:${timeMatch[2]}`
  }
  const at = parseApiDate(trimmed)
  if (!at.isValid()) return dash()
  return at.format('HH:mm')
}

export function formatRelative(value?: string | null): string {
  if (!value) return dash()
  syncDayjsLocale()
  const at = parseApiDate(value.trim())
  if (!at.isValid()) return dash()
  return at.fromNow()
}

/** Integer / count with vi-VN grouping (1.234.567). */
export function formatNumber(
  value?: number | string | null,
  options?: { minimumFractionDigits?: number; maximumFractionDigits?: number },
): string {
  const n = coerceNumber(value)
  if (n == null) return dash()
  return getNumberFormatter({
    minimumFractionDigits: options?.minimumFractionDigits ?? 0,
    maximumFractionDigits: options?.maximumFractionDigits ?? 0,
  }).format(n)
}

export function formatInteger(value?: number | string | null): string {
  return formatNumber(value)
}

export function formatDecimal(value?: number | string | null, fractionDigits = 2): string {
  return formatNumber(value, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })
}

/** VND amount — vi-VN grouping + VND suffix. */
export function formatVnd(amount?: number | string | null): string {
  const n = coerceNumber(amount)
  if (n == null) return dash()
  return `${formatNumber(n)} VND`
}

export function formatPercent(value?: number | string | null, fractionDigits = 1): string {
  const n = coerceNumber(value)
  if (n == null) return dash()
  return `${formatNumber(n, { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits })}%`
}

export function formatPhone(masked?: string | null): string {
  return formatPhoneDisplay(masked, dash())
}

export function sourceLabel(s: string | null | undefined): string {
  if (!s) return dash()
  const key = `sources.${s}`
  if (i18n.global.te(key)) return i18n.global.t(key)
  return s
}

export type ApplicationSourceFields = {
  source?: string | null
  platform?: { name?: string | null; code?: string | null } | null
  traffic_source?: {
    source_name?: string | null
    source_code?: string | null
    channel_name?: string | null
    channel_code?: string | null
    channel_type?: string | null
  } | null
  supplier_channel?: { channel_name?: string | null; channel_type?: string | null } | null
  supplier_unit?: { channel_name?: string | null } | null
  applied_channel?: { channel_name?: string | null; channel_type?: string | null; source_name?: string | null } | null
  supplier_submission_id?: number | null
}

const SUPPLIER_CHANNEL_TYPES = new Set(['agency', 'referral', 'school', 'campus', 'partner'])
const SUPPLIER_SOURCE_VALUES = new Set(['partner', 'school', 'referral'])
/** Approach platforms / NCC types shown as Phân bổ nguồn for supplier intake. */
const SUPPLIER_APPROACH_CODES = new Set(['agency', 'campus', 'school', 'referral', 'partner'])

function trafficSourceName(app: ApplicationSourceFields): string | null {
  const traffic = app.traffic_source
  const fromTraffic = traffic?.source_name?.trim() || traffic?.channel_name?.trim()
  if (fromTraffic) return fromTraffic

  const fromApplied = app.applied_channel?.source_name?.trim() || app.applied_channel?.channel_name?.trim()
  if (fromApplied) return fromApplied

  return null
}

/** Normalize NCC type → i18n key segment (Đại lý / Trường học / Giới thiệu). */
function normalizeSupplierApproachCode(raw: string | null | undefined): string | null {
  const code = raw?.trim().toLowerCase()
  if (!code || !SUPPLIER_APPROACH_CODES.has(code)) return null
  if (code === 'partner') return 'agency'
  if (code === 'campus') return 'school'
  return code
}

/** Localized Đại lý / Trường học / Giới thiệu from platform or channel_type. */
export function supplierApproachTypeLabel(typeOrCode: string | null | undefined): string | null {
  const code = normalizeSupplierApproachCode(typeOrCode)
  if (!code) return null
  const raw = typeOrCode?.trim().toLowerCase() ?? code
  const keys = [
    `channelPortal.channelTypes.${code}`,
    `settings.channels.types.${raw === 'campus' ? 'campus' : code}`,
    `settings.channels.types.${code}`,
  ]
  for (const key of keys) {
    if (i18n.global.te(key)) return String(i18n.global.t(key))
  }
  return null
}

/**
 * Phân bổ nguồn for NCC rows: Đại lý / Trường học / Giới thiệu
 * (not the supplier unit name, not Manual traffic fallback).
 */
export function applicationSupplierApproachLabel(app: ApplicationSourceFields): string | null {
  const fromPlatformCode = supplierApproachTypeLabel(app.platform?.code)
  if (fromPlatformCode) return fromPlatformCode

  const platformCode = app.platform?.code?.trim().toLowerCase()
  const platformName = app.platform?.name?.trim()
  if (platformName && platformCode && SUPPLIER_APPROACH_CODES.has(platformCode)) {
    return platformName
  }

  const channelType = app.supplier_channel?.channel_type?.trim()
    || app.supplier_unit?.channel_type?.trim()
    || app.applied_channel?.channel_type?.trim()
    || null
  return supplierApproachTypeLabel(channelType)
}

/** Supplier organization (NCC) name when the application came from supplier intake. */
export function applicationSupplierUnitLabel(app: ApplicationSourceFields): string | null {
  const nccName = app.supplier_channel?.channel_name?.trim() || app.supplier_unit?.channel_name?.trim()
  if (nccName) return nccName

  const channel = app.applied_channel
  const channelName = channel?.channel_name?.trim()
  if (!channelName) return null

  if (app.supplier_submission_id) return channelName
  if (app.source && SUPPLIER_SOURCE_VALUES.has(app.source)) return channelName
  if (channel?.channel_type && SUPPLIER_CHANNEL_TYPES.has(channel.channel_type)) return channelName

  return null
}

/** Department · team for internal (staff QR / manual) submitters. */
export function applicationInternalOrgLabel(app: {
  recruiter?: {
    department_name?: string | null
    department_code?: string | null
    team_name?: string | null
  } | null
}): string | null {
  const dept = app.recruiter?.department_name?.trim() || app.recruiter?.department_code?.trim() || ''
  const team = app.recruiter?.team_name?.trim() || ''
  if (dept && team) return `${dept} · ${team}`
  if (dept) return dept
  if (team) return team
  return null
}

/** Unit line under «Nguồn gửi»: NCC unit or internal phòng/nhóm. */
export function applicationSubmitUnitLabel(app: ApplicationSourceFields & {
  submit_origin?: 'supplier' | 'internal' | 'portal' | null
  recruiter?: {
    department_name?: string | null
    department_code?: string | null
    team_name?: string | null
  } | null
}): string | null {
  const origin = app.submit_origin
  const isSupplier = origin === 'supplier'
    || app.supplier_submission_id != null
    || Boolean(app.supplier_unit)
    || Boolean(app.supplier_channel)

  if (isSupplier && origin !== 'internal' && origin !== 'portal') {
    return applicationSupplierUnitLabel(app)
  }

  return applicationInternalOrgLabel(app) || applicationSupplierUnitLabel(app)
}

export function applicationPlatformLabel(app: ApplicationSourceFields): string | null {
  const approach = applicationSupplierApproachLabel(app)
  if (approach) return approach
  const name = app.platform?.name?.trim() || app.platform?.code?.trim()
  return name || null
}

/** Primary traffic source label (not NCC). */
export function applicationTrafficSourceLabel(app: ApplicationSourceFields): string {
  const traffic = trafficSourceName(app)
  if (traffic) return traffic
  return sourceLabel(app.source)
}

function isSupplierAttributionRow(app: ApplicationSourceFields & {
  submit_origin?: 'supplier' | 'internal' | 'portal' | null
}): boolean {
  if (app.submit_origin === 'supplier') return true
  if (app.submit_origin === 'internal' || app.submit_origin === 'portal') return false
  return app.supplier_submission_id != null
    || Boolean(app.supplier_unit)
    || Boolean(app.supplier_channel)
}

/** Primary attribution label for compact tables: NCC type (Đại lý/Trường) when supplier, else traffic. */
export function applicationSourceChannelLabel(app: ApplicationSourceFields & {
  submit_origin?: 'supplier' | 'internal' | 'portal' | null
}): string {
  const supplierUnit = applicationSupplierUnitLabel(app)
  const isSupplier = isSupplierAttributionRow(app)

  if (isSupplier) {
    const approach = applicationSupplierApproachLabel(app)
    if (approach) return approach
    if (supplierUnit) return supplierUnit
  }

  const traffic = applicationTrafficSourceLabel(app)
  if (traffic && traffic !== sourceLabel(app.source)) return traffic
  if (supplierUnit) return supplierUnit
  return traffic
}

/** Secondary line: NCC unit name, or localized source type / platform. */
export function applicationSourceChannelHint(app: ApplicationSourceFields & {
  submit_origin?: 'supplier' | 'internal' | 'portal' | null
}): string | null {
  if (isSupplierAttributionRow(app)) {
    return applicationSupplierUnitLabel(app)
  }

  const platform = applicationPlatformLabel(app)
  if (platform) return platform

  if (applicationSupplierUnitLabel(app) && app.source) {
    return sourceLabel(app.source)
  }

  if (trafficSourceName(app) && app.source) {
    return sourceLabel(app.source)
  }

  return null
}

export type TagLabelFields = {
  code?: string
  name?: string
  name_vi?: string | null
  name_en?: string | null
  name_zh?: string | null
}

export function tagLocalizedName(tag: TagLabelFields, locale?: string): string {
  const loc = locale ?? String(i18n.global.locale.value)
  if (loc === 'zh') return tag.name_zh || tag.name_vi || tag.name || tag.code || dash()
  if (loc === 'en') return tag.name_en || tag.name_vi || tag.name || tag.code || dash()
  return tag.name_vi || tag.name || tag.code || dash()
}

export function isoDateToDisplay(iso?: string | null): string {
  if (!iso?.trim() || !DATE_ONLY.test(iso.trim())) return ''
  const trimmed = iso.trim()
  if (!dayjs(trimmed, 'YYYY-MM-DD', true).isValid()) return ''
  return calendarDateFromYmd(trimmed)
}

export function displayDateToIso(display: string): string | null {
  const trimmed = display.trim()
  if (!trimmed) return null

  const dmY = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(trimmed)
  if (!dmY) return null

  const iso = `${dmY[3]}-${dmY[2]}-${dmY[1]}`
  return dayjs(iso, 'YYYY-MM-DD', true).isValid() ? iso : null
}

export function normalizeTimeInput(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return ''

  if (/^\d{1,2}:$/.test(trimmed)) {
    const hour = Number(trimmed.slice(0, -1))
    if (hour >= 0 && hour <= 23) {
      return `${String(hour).padStart(2, '0')}:00`
    }
    return ''
  }

  const match = TIME_ONLY.exec(trimmed)
  if (match) {
    const hour = Number(match[1])
    const minute = Number(match[2])
    if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
      return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
    }
    return ''
  }

  if (/^\d{3,4}$/.test(trimmed)) {
    const padded = trimmed.padStart(4, '0')
    const hour = Number(padded.slice(0, 2))
    const minute = Number(padded.slice(2, 4))
    if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
      return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
    }
  }

  return ''
}

type NextFollowTranslate = (key: string, params?: Record<string, unknown>) => string

/** Current instant in Vietnam timezone. */
export function nowInAppTimezone(): dayjs.Dayjs {
  return dayjs().tz(APP_TIMEZONE)
}

/** ISO calendar date N days before today in Vietnam timezone. */
export function isoDateDaysAgo(days: number): string {
  return nowInAppTimezone().subtract(days, 'day').format('YYYY-MM-DD')
}

/** Form value YYYY-MM-DDTHH:mm (VN local) → UTC ISO for API. */
export function formLocalDateTimeToApiIso(value: string): string | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const local = DATETIME_LOCAL.exec(trimmed)
  if (!local) return null
  const at = dayjs.tz(
    `${local[1]}-${local[2]}-${local[3]} ${local[4]}:${local[5]}`,
    'YYYY-MM-DD HH:mm',
    APP_TIMEZONE,
  )
  if (!at.isValid()) return null
  return at.utc().toISOString()
}

/** API UTC ISO → form value YYYY-MM-DDTHH:mm in Vietnam timezone. */
export function apiDateTimeToFormLocalValue(value?: string | null): string {
  if (!value?.trim()) return ''
  const trimmed = value.trim()
  if (DATE_ONLY.test(trimmed)) return `${trimmed}T09:00`
  const at = parseApiDate(trimmed)
  if (!at.isValid()) return ''
  return `${at.format('YYYY-MM-DD')}T${at.format('HH:mm')}`
}

/** Whether an API datetime is before now in Vietnam timezone. */
export function isApiDateTimeOverdue(value?: string | null): boolean {
  if (!value?.trim()) return false
  const at = parseApiDate(value.trim())
  if (!at.isValid()) return false
  return at.isBefore(nowInAppTimezone())
}

/** Next-follow instant as DD/MM/YYYY HH:mm. */
export function formatNextFollowAtLabel(
  value: string | null | undefined,
  _translate: NextFollowTranslate,
  options?: { emptyLabel?: string },
): string {
  const empty = options?.emptyLabel ?? dash()
  if (!value?.trim()) return empty
  const formatted = formatDateTime(value)
  return formatted === dash() ? empty : formatted
}

export function isoDateTimeLocalToDisplay(value?: string | null): string {
  if (!value?.trim()) return ''
  syncDayjsLocale()
  const trimmed = value.trim()

  // Form-local only (no Z / ±offset) — not API ISO timestamps
  if (DATETIME_LOCAL.test(trimmed) && trimmed.length === 16) {
    const local = DATETIME_LOCAL.exec(trimmed)
    if (!local) return ''
    const isoDate = `${local[1]}-${local[2]}-${local[3]}`
    if (!dayjs(isoDate, 'YYYY-MM-DD', true).isValid()) return ''
    const hour = Number(local[4])
    const minute = Number(local[5])
    if (hour > 23 || minute > 59) return ''
    return `${local[3]}/${local[2]}/${local[1]} ${local[4]}:${local[5]}`
  }

  const at = parseApiDate(trimmed.replace(' ', 'T'))
  if (at.isValid()) return at.format('DD/MM/YYYY HH:mm')
  return ''
}

export function displayDateTimeLocalToIso(display: string): string | null {
  const trimmed = display.trim()
  if (!trimmed) return null

  const match = DATETIME_DISPLAY.exec(trimmed)
  if (!match) return null

  const isoDate = `${match[3]}-${match[2]}-${match[1]}`
  if (!dayjs(isoDate, 'YYYY-MM-DD', true).isValid()) return null
  const hour = Number(match[4])
  const minute = Number(match[5])
  if (hour > 23 || minute > 59) return null
  return `${isoDate}T${match[4]}:${match[5]}`
}

/** Convenience bundle for `<script setup>`. */
export function useFormat() {
  return {
    APP_TIMEZONE,
    formatDate,
    formatDateTime,
    formatInterviewScheduledAt,
    formatTime,
    formatRelative,
    formatNumber,
    formatInteger,
    formatDecimal,
    formatVnd,
    parseMoneyInput,
    formatMoneyInputDisplay,
    formatPercent,
    formatPhone,
    sourceLabel,
    applicationSupplierUnitLabel,
    applicationInternalOrgLabel,
    applicationSubmitUnitLabel,
    applicationSourceChannelLabel,
    applicationSourceChannelHint,
    applicationTrafficSourceLabel,
    applicationPlatformLabel,
    tagLocalizedName,
    todayIsoDate,
    tomorrowIsoDate,
    addDaysIsoDate,
    nextVersionEffectiveFromIso,
    formatNowStamp,
    currentPeriodMonth,
    toBusinessIsoDate,
    ageFromBirthDate,
    displayWorkerAge,
    isoDateToDisplay,
    displayDateToIso,
    normalizeTimeInput,
    isoDateTimeLocalToDisplay,
    displayDateTimeLocalToIso,
    nowInAppTimezone,
    isoDateDaysAgo,
    formLocalDateTimeToApiIso,
    apiDateTimeToFormLocalValue,
    isApiDateTimeOverdue,
    formatNextFollowAtLabel,
  }
}
